/**
 * Ein Aufklapp-Baum über `GET /entities/tree` zur Auswahl eines Objekts —
 * `access-invite-entity` (Spec 05a, „Sheet Person einladen") **und**
 * `task-cost-center` (Spec 06a). `GET /entities/tree` kennt keine
 * Volltextsuche (anders als `GET /tenants?q=` für `TenantPicker`) — die
 * einzige Form, in der die API Objekte hergibt, ist eine Ebene nach
 * `parent_id`, dieselbe, die `features/objects/ObjectTree` in der
 * Seitenleiste zeigt.
 *
 * Bis 006a gab es hiervon drei fast identische Kopien —
 * `features/access/EntityPicker.tsx`, `features/tasks/
 * TaskCostCenterField.tsx::CostCenterTree` und `features/objects/api.ts::
 * useEntitiesTree` je einmal für die Abfrage. Geteilte Oberfläche gehört
 * nach `components/` (`apps/web/CLAUDE.md`), deshalb liegt sie jetzt hier,
 * mit der Abfrage in `lib/entities.ts` (siehe dort, warum nicht in einem
 * `features/*\/api.ts`). `features/objects/api.ts::useEntitiesTree` bleibt
 * unverändert — dieselbe Abfrage ein drittes Mal, vor 006a entstanden und
 * nicht Teil dieses Befunds.
 *
 * Ausgewählt zeigt das Feld die Zeile mit „Ändern" (Pflichtfeld, dieselbe
 * Form wie `features/contracts/TenantPicker.tsx`) — oder, mit `allowClear`,
 * einen Entfernen-Knopf statt dessen: `task-cost-center` ist optional und
 * muss sich wieder auf „keine Kostenstelle" zurücksetzen lassen (Spec 06a,
 * AK-391), `access-invite-entity` verlangt dagegen immer ein Objekt.
 *
 * **Seit LUK-48: Vorauswahl mit aufgeklappten Vorfahren.** Öffnet der Baum
 * (Klick auf „Ändern", oder direkt beim Anlegen ohne Vorbelegung) mit
 * gesetztem `value`, lädt er dessen Pfad über `useEntityHead` (Cache geteilt
 * mit dem Objektbildschirm) und klappt jeden Vorfahren initial auf — die
 * gewählte Zeile trägt `aria-selected`, eine Akzentfläche und ein Häkchen,
 * und scrollt sich in den sichtbaren Bereich. Ein leerer/ungültiger Wert
 * (`id: ''`, wie `DocumentSubjectField`s Sentinel „Eingang") lässt die
 * Abfrage aus; ein 403/404/archiviertes Ziel lässt den Baum zugeklappt, ohne
 * einen Fehler zu zeigen (Ticket LUK-48) — `rootQuery` bleibt davon
 * unberührt und zeigt weiterhin ganz normal die Wurzelebene.
 *
 * **Seit Spec 09: ein opt-in Suchmodus** (`searchable`, `kinds`), für
 * `document-subject-picker` (Spec 09, „Der Picker bekommt einen
 * Suchmodus"). Beide Eigenschaften haben Vorgaben, die das bisherige
 * Verhalten ergeben — `access-invite-entity` und `task-cost-center` rufen
 * diese Komponente unverändert auf und zeigen den Suchmodus deshalb nie
 * (AK-571, „keine bestehende Journey ändert ihre Aussage", Regel 9). Ist
 * `searchable` gesetzt, trägt das Suchfeld oben zusätzlich zum gewohnten
 * Baum: leer bleibt der Baum unverändert sichtbar, ab zwei Zeichen ersetzt
 * ihn die flache Trefferliste aus `lib/entities.ts::useEntityTreeSearch`.
 */

import { Check, ChevronDown, ChevronRight, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import type { ReactNode, Ref } from 'react'

import { EmptyState } from '@/components/states/EmptyState'
import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import { expandedAncestorIds, useEntityHead, useEntityTreeLevel, useEntityTreeSearch } from '@/lib/entities'
import type { EntityTreeNode } from '@/lib/entities'
import { FIELD_CLASS } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'

export interface EntitySelection {
  readonly id: string
  readonly label: string
}

/** Der Mindestlänge des Suchtexts, ab der der Server umschaltet (Spec 09,
 * „Die Regeln des Modus": „q ab zwei Zeichen; darunter 422"). Lokal
 * geprüft, damit kein zu kurzer Text überhaupt erst angefragt wird. */
const MIN_SEARCH_LENGTH = 2

interface EntityPickerProps {
  readonly testId: string
  /** Die Beschriftung über dem Feld — „Zugriff auf" im Einladen-Sheet,
   * „Kostenstelle" bei `task-cost-center`; die beiden Verwendungen meinen
   * fachlich verschiedene Dinge, nur der Baum darunter ist identisch. */
  readonly label: string
  readonly value: EntitySelection | null
  readonly onSelect: (entity: EntitySelection) => void
  readonly error: FieldError | undefined
  readonly errorPrefix: string
  readonly apiField?: string
  readonly disabled?: boolean
  /** Zeigt statt „Ändern" einen eigenen Entfernen-Knopf (`${testId}-clear`)
   * für optionale Felder — `onClear` ist dann Pflicht. */
  readonly allowClear?: boolean
  readonly onClear?: () => void
  /** Für einen programmatischen Autofokus beim Öffnen eines Sheets (Spec 07,
   * `duedate-subject-picker`: „erstes, fokussiertes Feld") — `tabIndex={-1}`
   * am Wurzelelement macht das möglich, ohne einen zusätzlichen Stopp in der
   * normalen Tab-Reihenfolge einzufügen. */
  readonly ref?: Ref<HTMLDivElement>
  /** Opt-in (Spec 09): schaltet das Suchfeld und die flache Trefferliste
   * dazu. Vorgabe `false` — das bisherige Verhalten. */
  readonly searchable?: boolean
  /** `kind=`-Werte für den Suchmodus, z. B. `ALLOWED_PARENT_KINDS["document"]`
   * (`DocumentSubjectField.tsx`). Ohne Wirkung, solange `searchable` fehlt. */
  readonly kinds?: readonly string[]
  readonly searchPlaceholder?: string
  readonly emptySearchTitle?: string
  readonly emptySearchHint?: string
  /** Ein zusätzlicher, immer sichtbarer Eintrag ganz oben in Baum **und**
   * Trefferliste — bei `document-subject-picker` der Weg zurück zu
   * „Eingang" (Spec 09, „Der Objektpicker": „damit die Vorbelegung wieder
   * erreichbar ist"). Generisch statt eines fest verdrahteten „Eingang",
   * weil `EntityPicker` das Datenmodell dahinter nicht kennt. */
  readonly extraOption?: { readonly testId: string; readonly label: string; readonly onSelect: () => void }
  /** Seit Spec 24c (`sender-subject-picker`): Ist gesetzt **und** `value`
   * `null`, zeigt das Feld eine geschlossene Zeile mit diesem Text statt
   * sofort zu öffnen — für ein optionales Feld, das die meiste Zeit leer
   * bleibt (`default_subject_id`). Ohne `placeholder` bleibt das bisherige
   * Verhalten unverändert (`access-invite-entity`, `task-cost-center`,
   * `document-subject-picker` setzen ihn nie, Regel 9). */
  readonly placeholder?: string
}

export function EntityPicker({
  testId,
  label,
  value,
  onSelect,
  error,
  errorPrefix,
  apiField = 'entity_id',
  disabled = false,
  allowClear = false,
  onClear,
  ref,
  searchable = false,
  kinds = [],
  searchPlaceholder = 'Suchen',
  emptySearchTitle = 'Nichts gefunden.',
  emptySearchHint,
  extraOption,
  placeholder,
}: EntityPickerProps) {
  const opensImmediatelyWhenEmpty = placeholder === undefined
  const [isSearching, setIsSearching] = useState(value === null && opensImmediatelyWhenEmpty)
  const [q, setQ] = useState('')
  // Ein von außen gesetzter Wert (z. B. `presetEntity` aus `access-add-invite`,
  // Spec 05a: „mit dem Objekt bereits gesetzt") schließt die Auswahl —
  // während des Renderns verglichen, nicht in einem Effekt (dasselbe React-
  // Muster wie `TenantPicker`).
  const [syncedValueId, setSyncedValueId] = useState(value?.id)
  if (value?.id !== syncedValueId) {
    setSyncedValueId(value?.id)
    setIsSearching(value === null && opensImmediatelyWhenEmpty)
    setQ('')
  }

  const isSearchMode = searchable && q.trim().length >= MIN_SEARCH_LENGTH
  const rootQuery = useEntityTreeLevel(undefined, { enabled: isSearching && !isSearchMode })
  const searchQuery = useEntityTreeSearch(q.trim(), kinds, { enabled: isSearching && isSearchMode })
  const hasError = error?.field === apiField
  const errorTestId = `${errorPrefix}-error-${apiField}`

  // Ein leerer/ungültiger Wert (`DocumentSubjectField`s Sentinel „Eingang",
  // `id: ''`) ist keine echte Vorauswahl — `realValue` filtert ihn heraus,
  // statt gegen `/entities/` zu laufen.
  const realValue = value !== null && value.id !== '' ? value : null
  const shouldLoadAncestry = isSearching && !isSearchMode && realValue !== null
  const ancestryQuery = useEntityHead(realValue?.id, { enabled: shouldLoadAncestry })
  // Erst wenn diese Abfrage fertig ist (egal ob erfolgreich oder mit
  // Fehler), steht `expandIds` fest — vorher gar nicht erst die Zeilen
  // mounten (`rootQuery.isSuccess` reicht dafür nicht: Bei einem bereits
  // zwischengespeicherten Baum — derselbe Picker war in dieser Sitzung schon
  // einmal offen — liefert `useEntityTreeLevel` sofort `isSuccess: true` aus
  // dem Cache, unabhängig davon, ob `useEntityHead` seine Antwort schon hat.
  // `EntityPickerRow`s `useState`-Initialisierer läse dann ein noch leeres
  // `expandIds` und bliebe zugeklappt, für immer — ein späteres Update des
  // Werts erreicht einen bereits ausgewerteten Initialisierer nicht mehr).
  const ancestryReady = !shouldLoadAncestry || ancestryQuery.isSuccess || ancestryQuery.isError

  const ancestry = ancestryQuery.isSuccess ? ancestryQuery.data : undefined
  const expandIds = expandedAncestorIds(ancestry)
  // Kein Treffer für die Zeile, solange `ancestry` nicht (sichtbar,
  // unarchiviert) geladen ist — dieselbe Bedingung wie `expandedAncestorIds`,
  // hier zusätzlich für die Hervorhebung der eigenen Zeile gebraucht.
  const selectedId = ancestry?.archived_at === null ? realValue?.id : undefined

  function selectEntity(entity: EntitySelection) {
    onSelect(entity)
    setIsSearching(false)
    setQ('')
  }

  const extraOptionRow = extraOption !== undefined && (
    <li className="border-border border-b last:border-b-0">
      <button
        type="button"
        data-testid={extraOption.testId}
        onClick={() => {
          extraOption.onSelect()
          setIsSearching(false)
          setQ('')
        }}
        className="hover:bg-bg flex min-h-(--tap-min) w-full items-center px-2 text-left transition-colors duration-(--duration-fast) ease-(--ease-out)"
      >
        <span className="text-body text-text">{extraOption.label}</span>
      </button>
    </li>
  )

  return (
    <div className="flex flex-col gap-1" data-testid={testId} ref={ref} tabIndex={-1}>
      <span className="text-label text-muted">{label}</span>

      {!isSearching && (value !== null || placeholder !== undefined) ? (
        <div className={`flex items-center justify-between gap-2 ${FIELD_CLASS}`}>
          <span className={value !== null ? 'text-text truncate' : 'text-muted truncate'}>
            {value?.label ?? placeholder}
          </span>
          {allowClear && value !== null ? (
            <button
              type="button"
              data-testid={`${testId}-clear`}
              aria-label="Auswahl entfernen"
              onClick={onClear}
              className="text-muted hover:text-text flex min-h-(--tap-min) min-w-(--tap-min) shrink-0 items-center justify-center"
            >
              <X className="size-3.5" />
            </button>
          ) : (
            !disabled && (
              <button
                type="button"
                data-testid={`${testId}-change`}
                onClick={() => {
                  setIsSearching(true)
                }}
                className="text-accent text-label inline-flex min-h-(--tap-min) min-w-(--tap-min) items-center justify-center hover:underline"
              >
                Ändern
              </button>
            )
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {searchable && (
            <input
              type="search"
              data-testid={`${testId}-search`}
              aria-label={searchPlaceholder}
              placeholder={searchPlaceholder}
              value={q}
              onChange={(event) => {
                setQ(event.target.value)
              }}
              className={`text-text ${FIELD_CLASS}`}
            />
          )}

          {isSearchMode ? (
            <EntitySearchResults
              testId={testId}
              query={searchQuery}
              extraOptionRow={extraOptionRow}
              emptyTitle={emptySearchTitle}
              {...(emptySearchHint !== undefined ? { emptyHint: emptySearchHint } : {})}
              onSelect={selectEntity}
            />
          ) : (
            <div className="border-border rounded-card max-h-64 overflow-y-auto border">
              {(rootQuery.isPending || !ancestryReady) && <SkeletonRows testId={`${testId}-loading`} rows={3} />}

              {ancestryReady && rootQuery.isError && (
                <ErrorState
                  testId={`${testId}-error`}
                  retryTestId={`${testId}-retry`}
                  error={rootQuery.error}
                  onRetry={() => {
                    void rootQuery.refetch()
                  }}
                />
              )}

              {ancestryReady && rootQuery.isSuccess && rootQuery.data.items.length === 0 && extraOption === undefined && (
                <EmptyState testId={`${testId}-empty`} title="Kein Objekt angelegt." />
              )}

              {ancestryReady && rootQuery.isSuccess && (rootQuery.data.items.length > 0 || extraOption !== undefined) && (
                <ul>
                  {extraOptionRow}
                  {rootQuery.data.items.map((node) => (
                    <EntityPickerRow
                      key={node.id}
                      node={node}
                      depth={0}
                      testId={testId}
                      onSelect={selectEntity}
                      expandIds={expandIds}
                      selectedId={selectedId}
                    />
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {hasError && (
        <p id={errorTestId} data-testid={errorTestId} role="alert" className="text-status-overdue">
          {error.message}
        </p>
      )}
    </div>
  )
}

interface EntitySearchResultsProps {
  readonly testId: string
  readonly query: ReturnType<typeof useEntityTreeSearch>
  readonly extraOptionRow: ReactNode
  readonly emptyTitle: string
  readonly emptyHint?: string
  readonly onSelect: (entity: EntitySelection) => void
}

/** Zweite Zeile einer Trefferzeile: `path_label · kind.label`, oder nur
 * `kind.label`, wenn kein Pfad da ist — ein Grundstück hat keine
 * `ancestor_ids` und damit `path_label === ''` (`db/entities.py`, keine
 * eigene Entscheidung dieser Datei), Serialisierung macht das Feld zusätzlich
 * optional/`null`. Ohne diese Weiche stand „· Grundstück" da (W-D4,
 * `reports/18-design-reviewer.md`) — derselbe Fehler wie W-B1 auf iOS, nur an
 * dieser Trefferzeile statt an der Dokumentzuordnung. */
function searchResultHint(node: EntityTreeNode): string {
  const pathLabel = node.path_label ?? ''
  return pathLabel === '' ? node.kind.label : `${pathLabel} · ${node.kind.label}`
}

/** Die flache Trefferliste des Suchmodus (Spec 09, „Der Objektpicker"): Label
 * fett, darunter `path_label` und die Ebene (`kind.label`) in `--muted` —
 * „Wohnung 3" gibt es zwölfmal, ohne den Pfad ist eine Zeile nicht
 * zuzuordnen. */
function EntitySearchResults({ testId, query, extraOptionRow, emptyTitle, emptyHint, onSelect }: EntitySearchResultsProps) {
  return (
    <div className="border-border rounded-card max-h-64 overflow-y-auto border">
      {query.isPending && <SkeletonRows testId={`${testId}-loading`} rows={3} />}

      {query.isError && (
        <ErrorState
          testId={`${testId}-error`}
          retryTestId={`${testId}-retry`}
          error={query.error}
          onRetry={() => {
            void query.refetch()
          }}
        />
      )}

      {query.isSuccess && query.data.items.length === 0 && (
        <>
          {extraOptionRow !== false && <ul>{extraOptionRow}</ul>}
          <EmptyState testId={`${testId}-empty`} title={emptyTitle} {...(emptyHint !== undefined ? { hint: emptyHint } : {})} />
        </>
      )}

      {query.isSuccess && query.data.items.length > 0 && (
        <ul>
          {extraOptionRow}
          {query.data.items.map((node) => (
            <li key={node.id} className="border-border border-b last:border-b-0">
              <button
                type="button"
                data-testid={`${testId}-result-${node.id}`}
                onClick={() => {
                  onSelect({ id: node.id, label: node.label })
                }}
                className="hover:bg-bg flex min-h-(--tap-min) w-full flex-col items-start justify-center gap-0.5 px-2 py-1 text-left transition-colors duration-(--duration-fast) ease-(--ease-out)"
              >
                {/* `overview?.headline` statt `label` (Spec 31, Fachregel 5):
                 * ein Vertrag zeigt hier den Mieternamen, jede andere Art
                 * bleibt unverändert bei ihrem `label`. */}
                <span className="text-body text-text font-medium">{node.overview?.headline ?? node.label}</span>
                <span className="text-label text-muted">{searchResultHint(node)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

interface EntityPickerRowProps {
  readonly node: EntityTreeNode
  readonly depth: number
  readonly testId: string
  readonly onSelect: (entity: EntitySelection) => void
  /** Knoten-Ids, die beim Mounten bereits aufgeklappt starten sollen — die
   * Vorfahren einer Vorauswahl (LUK-48). Nur die **erste** Auswertung zählt
   * (`useState`-Initialisierer): Öffnet der Baum erneut, montiert die ganze
   * Zeile neu und liest die inzwischen aktuelle Menge frisch ein. */
  readonly expandIds: ReadonlySet<string>
  /** Die Id der Vorauswahl, für Hervorhebung und Scroll — `undefined`, wenn
   * keine gilt (kein Wert, Sentinel, nicht sichtbar/archiviert/404). */
  readonly selectedId: string | undefined
}

function EntityPickerRow({ node, depth, testId, onSelect, expandIds, selectedId }: EntityPickerRowProps) {
  const [isExpanded, setIsExpanded] = useState(() => expandIds.has(node.id))
  const childrenQuery = useEntityTreeLevel(node.id, { enabled: isExpanded })
  const isSelected = node.id === selectedId
  const rowRef = useRef<HTMLLIElement>(null)

  // Läuft nur, wenn diese Zeile tatsächlich die Vorauswahl ist — sie
  // entsteht erst, sobald ihr letzter aufgeklappter Vorfahre seine Kinder
  // geladen hat; das Mounten selbst ist bereits der richtige Zeitpunkt zum
  // Scrollen, kein zusätzliches Abwarten nötig.
  useEffect(() => {
    if (isSelected) rowRef.current?.scrollIntoView({ block: 'nearest' })
  }, [isSelected])

  return (
    <li ref={rowRef} className="border-border border-b last:border-b-0">
      <div className="flex h-(--row-h) items-center" style={{ paddingLeft: `${String(depth * 16)}px` }}>
        {node.has_children ? (
          <button
            type="button"
            data-testid={`${testId}-toggle-${node.id}`}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? `${node.label} einklappen` : `${node.label} aufklappen`}
            onClick={() => {
              setIsExpanded((current) => !current)
            }}
            className="text-muted hover:text-text flex h-(--row-h) w-(--row-h) shrink-0 items-center justify-center transition-colors duration-(--duration-fast) ease-(--ease-out)"
          >
            {isExpanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          </button>
        ) : (
          <span aria-hidden="true" className="h-(--row-h) w-(--row-h) shrink-0" />
        )}
        <button
          type="button"
          role="option"
          data-testid={`${testId}-option-${node.id}`}
          aria-selected={isSelected}
          onClick={() => {
            onSelect({ id: node.id, label: node.label })
          }}
          className={`text-body text-text flex h-(--row-h) flex-1 items-center justify-between gap-2 px-2 text-left transition-colors duration-(--duration-fast) ease-(--ease-out) ${
            isSelected ? 'bg-accent/10' : 'hover:bg-bg'
          }`}
        >
          <span className="truncate">{node.label}</span>
          {isSelected && <Check aria-hidden="true" className="text-accent size-3.5 shrink-0" />}
        </button>
      </div>

      {isExpanded && (
        <ul>
          {childrenQuery.isPending && (
            <div style={{ paddingLeft: `${String((depth + 1) * 16)}px` }}>
              <SkeletonRows testId={`${testId}-loading-${node.id}`} rows={2} />
            </div>
          )}

          {childrenQuery.isError && (
            <div style={{ paddingLeft: `${String((depth + 1) * 16)}px` }}>
              <ErrorState
                testId={`${testId}-error-${node.id}`}
                retryTestId={`${testId}-retry-${node.id}`}
                error={childrenQuery.error}
                onRetry={() => {
                  void childrenQuery.refetch()
                }}
              />
            </div>
          )}

          {childrenQuery.isSuccess &&
            childrenQuery.data.items.map((child) => (
              <EntityPickerRow
                key={child.id}
                node={child}
                depth={depth + 1}
                testId={testId}
                onSelect={onSelect}
                expandIds={expandIds}
                selectedId={selectedId}
              />
            ))}
        </ul>
      )}
    </li>
  )
}
